import { MultilingualWidgetConfig } from '../dashboard.widgets.config';

interface WeekData {
  range: {
    en: string;
    de: string;
  };
  days: Day[];
}

interface Day {
  name: {
    en: string;
    de: string;
  };
  number: number;
  active: boolean;
}

interface StaffCategory {
  name: {
    en: string;
    de: string;
  };
  staff: StaffMember[];
}

interface StaffMember {
  name: {
    en: string;
    de: string;
  };
  shift: 'AM' | 'PM' | 'Night';
  shiftLabel: {
    en: {
      AM: string;
      PM: string;
      Night: string;
    };
    de: {
      AM: string;
      PM: string;
      Night: string;
    };
  };
}

interface ButtonLabels {
  previous: {
    en: string;
    de: string;
  };
  next: {
    en: string;
    de: string;
  };
}

// Data for the staff roster
const weekData: WeekData = {
  range: {
    en: 'March 17-23, 2025',
    de: '17.-23. März 2025',
  },
  days: [
    { name: { en: 'Mon', de: 'Mo' }, number: 17, active: true },
    { name: { en: 'Tue', de: 'Di' }, number: 18, active: false },
    { name: { en: 'Wed', de: 'Mi' }, number: 19, active: false },
    { name: { en: 'Thu', de: 'Do' }, number: 20, active: false },
    { name: { en: 'Fri', de: 'Fr' }, number: 21, active: false },
  ],
};

const staffCategories: StaffCategory[] = [
  {
    name: { en: 'Doctors', de: 'Ärzte' },
    staff: [
      {
        name: { en: 'Dr. Emma Reynolds', de: 'Dr. Emma Weber' },
        shift: 'AM',
        shiftLabel: {
          en: { AM: 'AM', PM: 'PM', Night: 'Night' },
          de: { AM: 'VM', PM: 'NM', Night: 'Nacht' },
        },
      },
      {
        name: { en: 'Dr. James Wilson', de: 'Dr. Jakob Wilke' },
        shift: 'PM',
        shiftLabel: {
          en: { AM: 'AM', PM: 'PM', Night: 'Night' },
          de: { AM: 'VM', PM: 'NM', Night: 'Nacht' },
        },
      },
      {
        name: { en: 'Dr. Sophia Chen', de: 'Dr. Sophia Krüger' },
        shift: 'Night',
        shiftLabel: {
          en: { AM: 'AM', PM: 'PM', Night: 'Night' },
          de: { AM: 'VM', PM: 'NM', Night: 'Nacht' },
        },
      },
    ],
  },
  {
    name: { en: 'Nurses', de: 'Pflegepersonal' },
    staff: [
      {
        name: { en: 'Michael Thompson, RN', de: 'Michael Thieme, PK' },
        shift: 'AM',
        shiftLabel: {
          en: { AM: 'AM', PM: 'PM', Night: 'Night' },
          de: { AM: 'VM', PM: 'NM', Night: 'Nacht' },
        },
      },
      {
        name: { en: 'Lisa Garcia, RN', de: 'Lisa Gerhardt, PK' },
        shift: 'AM',
        shiftLabel: {
          en: { AM: 'AM', PM: 'PM', Night: 'Night' },
          de: { AM: 'VM', PM: 'NM', Night: 'Nacht' },
        },
      },
      {
        name: { en: 'Robert Kim, RN', de: 'Robert Klein, PK' },
        shift: 'PM',
        shiftLabel: {
          en: { AM: 'AM', PM: 'PM', Night: 'Night' },
          de: { AM: 'VM', PM: 'NM', Night: 'Nacht' },
        },
      },
      {
        name: { en: 'Sarah Johnson, RN', de: 'Sarah Jäger, PK' },
        shift: 'PM',
        shiftLabel: {
          en: { AM: 'AM', PM: 'PM', Night: 'Night' },
          de: { AM: 'VM', PM: 'NM', Night: 'Nacht' },
        },
      },
      {
        name: { en: 'David Martinez, RN', de: 'David Martens, PK' },
        shift: 'Night',
        shiftLabel: {
          en: { AM: 'AM', PM: 'PM', Night: 'Night' },
          de: { AM: 'VM', PM: 'NM', Night: 'Nacht' },
        },
      },
    ],
  },
];

const buttonLabels: ButtonLabels = {
  previous: {
    en: 'Previous Week',
    de: 'Vorherige Woche',
  },
  next: {
    en: 'Next Week',
    de: 'Nächste Woche',
  },
};

// Color mapping for shift types
const shiftColors = {
  AM: '#10b981',
  PM: '#434ce8',
  Night: '#f59e0b',
};

const generateRosterHtml = (language: 'en' | 'de'): string => {
  // Generate the days of the week
  const daysHtml = weekData.days
    .map(
      day => `
      <div style="display: flex; flex-direction: column; align-items: center; min-width: 80px; margin-right: ${
        day === weekData.days[weekData.days.length - 1] ? '0' : '8px'
      };">
        <div style="font-weight: 600; color: ${
          day.active ? '#434ce8' : '#686b87'
        }; margin-bottom: 8px;">${day.name[language]}</div>
        <div style="width: 42px; height: 42px; border-radius: 50%; background-color: #ecf3fe; display: flex; align-items: center; justify-content: center; margin-bottom: 8px;">
          <div style="font-weight: 500;">${day.number}</div>
        </div>
      </div>
    `,
    )
    .join('');

  // Generate staff categories and members
  const staffCategoriesHtml = staffCategories
    .map(
      (category, index) => `
      <div style="${index < staffCategories.length - 1 ? 'margin-bottom: 16px;' : ''}">
        <div style="font-weight: 600; color: #686b87; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #eff1f6;">${
          category.name[language]
        }</div>
        <div style="display: flex; flex-direction: column; gap: 8px;">
          ${category.staff
            .map(
              member => `
              <div style="display: flex; justify-content: space-between; padding: 8px; background-color: #fdfcff; border-radius: 8px; border: 1px solid #eff1f6;">
                <div style="font-weight: 500;">${member.name[language]}</div>
                <div style="color: ${shiftColors[member.shift]}; font-weight: 500;">${
                  member.shiftLabel[language][member.shift]
                }</div>
              </div>
            `,
            )
            .join('')}
        </div>
      </div>
    `,
    )
    .join('');

  return `
    <div style="display: flex; flex-direction: column; height: 100%; font-family: 'Be Vietnam Pro', sans-serif; color: #282a32;">
      <div style="margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center;">
        <div style="font-weight: 600; color: #282a32;">${
          language === 'en' ? 'Staff Roster' : 'Dienstplan'
        }</div>
        <div style="font-size: 0.85rem; color: #686b87;">${weekData.range[language]}</div>
      </div>
      <div style="display: flex; overflow-x: auto; margin-bottom: 12px; padding-bottom: 4px;">
        ${daysHtml}
      </div>
      <div style="flex: 1; overflow-y: auto;">
        ${staffCategoriesHtml}
      </div>
      <div style="margin-top: 12px; display: flex; justify-content: space-between;">
        <button style="background-color: transparent; color: #434ce8; border: 1px solid #434ce8; border-radius: 8px; padding: 8px 16px; font-size: 0.85rem; font-weight: 500; cursor: pointer; font-family: 'Be Vietnam Pro', sans-serif;">${
          buttonLabels.previous[language]
        }</button>
        <button style="background-color: #434ce8; color: white; border: none; border-radius: 8px; padding: 8px 16px; font-size: 0.85rem; font-weight: 500; cursor: pointer; font-family: 'Be Vietnam Pro', sans-serif;">${
          buttonLabels.next[language]
        }</button>
      </div>
    </div>`;
};

export const staffRosterWidget: MultilingualWidgetConfig = {
  id: 'staff-roster',
  translations: {
    en: {
      title: 'Weekly Staff Roster',
      content: generateRosterHtml('en'),
    },
    de: {
      title: 'Wöchentlicher Dienstplan',
      content: generateRosterHtml('de'),
    },
  },
  x: 0,
  y: 0,
  w: 2,
  h: 4,
};
